import type { PropsWithChildren, ReactNode } from 'react';

import { fn, nat, option, pipe, remote } from '@code-expert/prelude';
import { GuardOption, useDeepCompareMemo } from '@code-expert/react';
import type { CourseUrl, SemesterId, UserPublic } from '/imports/domain';
import { formatRoute } from '/imports/modules/Routing';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import { useUserInboxContext } from '/imports/ui/components/Messages/UserInboxContext';
import { getProfileMenuUser, ProfileMenu } from '/imports/ui/components/ProfileMenu';
import { TopNavEditor } from '/imports/ui/components/TopNav/TopNavEditor';
import { TopNavLayoutWrapper } from '/imports/ui/layouts/TopNavLayout';
import { examsRoute } from '/imports/ui/pages/myCourses/routes';
import { myExamsRoute } from './routes';

export interface ExamEditorLayoutProps {
  examName?: string;
  utils?: ReactNode;
  semester: SemesterId;
  courseUrl: CourseUrl;
}

export const ExamEditorLayout = ({
  courseUrl,
  semester,
  examName,
  children,
}: PropsWithChildren<ExamEditorLayoutProps>) => {
  const { user } = useGlobalContext();
  const { examMode } = useGlobalContext();

  return (
    <TopNavLayoutWrapper
      menu={
        <TopNavEditor
          name="Exams"
          title={examName}
          closeLink={
            examMode
              ? formatRoute(myExamsRoute)({})
              : formatRoute(examsRoute)({ semester, courseUrl })
          }
          utils={
            <GuardOption
              value={option.fromNullable(user)}
              alt={() => <ProfileMenu user={option.none} />}
              render={(user) => <ProfileMenuAuthenticated user={user} />}
            />
          }
        />
      }
    >
      {children}
    </TopNavLayoutWrapper>
  );
};

const ProfileMenuAuthenticated = ({ user }: { user: UserPublic }) => {
  const { userMessages, userNotifications } = useUserInboxContext();
  const hasNotifications = useDeepCompareMemo(
    () =>
      pipe(
        remote.sequenceT(userMessages.count, userNotifications.count),
        remote.map(fn.tupled(nat.SumMonoid.concat)),
        remote.exists((x) => x > 0),
      ),

    [userMessages.count, userNotifications.count],
  );
  const userMenuUser = getProfileMenuUser(user, hasNotifications);
  return <ProfileMenu user={option.some(userMenuUser)} />;
};
